/**
 * @file CustomerAddressesSection.tsx
 * @description CRUD адресов клиента (phase08.5 shadcn).
 */

import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { AddressEditDialog } from "../../../../components/customers/AddressEditDialog";
import { Alert } from "../../../../components/ui/alert";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { Separator } from "../../../../components/ui/separator";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { ApiError } from "../../../../lib/api-client";
import { addresses, type AddressDto } from "../../../../lib/api-customers";

interface Props {
  customerId: string;
  canManage: boolean;
}

const KIND_LABEL = {
  SHIPPING: "Доставка",
  BILLING: "Платёжный",
  PICKUP: "Самовывоз",
} as const;

export function CustomerAddressesSection({ customerId, canManage }: Props) {
  const notify = useNotify();
  const [items, setItems] = useState<AddressDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AddressDto | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setItems(await addresses.listForCustomer(customerId));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function onDelete(addr: AddressDto) {
    if (!confirm(`Удалить адрес «${addr.fullAddress.slice(0, 50)}…»?`)) return;
    try {
      await addresses.remove(addr.id);
      notify.success("Адрес удалён");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div className="flex items-center justify-between mb-3">
          <div className="label-editorial">Адреса</div>
          {canManage ? (
            <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              Добавить
            </Button>
          ) : null}
        </div>

        {loading ? (
          <p className="text-sm text-on-surface-muted">Загрузка…</p>
        ) : items.length === 0 ? (
          <Alert severity="info">Адресов нет.</Alert>
        ) : (
          <div>
            {items.map((a, idx) => (
              <div key={a.id}>
                {idx > 0 ? <Separator /> : null}
                <div className="flex items-start gap-3 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="default">{KIND_LABEL[a.kind]}</Badge>
                      {a.isDefault ? (
                        <Badge variant="primary">по умолчанию</Badge>
                      ) : null}
                      {a.dadataSyncedAt ? (
                        <Badge variant="outline">DaData</Badge>
                      ) : null}
                    </div>
                    <p className="text-sm text-on-surface mt-1">{a.fullAddress}</p>
                    {a.postalCode ? (
                      <p className="text-xs text-on-surface-variant">
                        Индекс: {a.postalCode}
                      </p>
                    ) : null}
                  </div>
                  {canManage ? (
                    <div className="flex gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setEditing(a)}
                        aria-label="Редактировать"
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => void onDelete(a)}
                        aria-label="Удалить"
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {(creating || editing) && (
          <AddressEditDialog
            open
            customerId={customerId}
            address={editing}
            onClose={() => {
              setCreating(false);
              setEditing(null);
            }}
            onSaved={() => {
              setCreating(false);
              setEditing(null);
              void refresh();
            }}
          />
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
